import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { whyChooseList } from '@/mocks/homeData';

export default function WhyChooseSection() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const toggleExpand = (index: number) => {
    setExpandedIndex((prev) => (prev === index ? null : index));
  };

  return (
    <section
      ref={sectionRef}
      className="relative w-full py-16 md:py-24 bg-background-100"
    >
      <div className="w-full px-4 md:px-8 lg:px-12 max-w-7xl mx-auto">
        <div className="text-center mb-10 md:mb-14">
          <span className="inline-block text-accent-600 text-sm font-medium tracking-wider uppercase mb-2">
            Why Choose
          </span>
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
            {t('why.title')}
          </h2>
        </div>

        <div
          className={`grid grid-cols-1 md:grid-cols-2 items-start gap-4 md:gap-6 md:px-10 lg:px-16 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {whyChooseList.map((item, index) => {
            const isExpanded = expandedIndex === index;
            return (
              <button
                key={index}
                type="button"
                onClick={() => toggleExpand(index)}
                className="text-left flex flex-col justify-center bg-background-50 rounded-xl p-5 md:p-7 border border-background-200/70 hover:border-accent-300 transition-all duration-300 cursor-pointer"
              >
                <div className="flex flex-col">
                  <div className="flex items-center gap-4">
                    <span className="w-10 h-10 flex items-center justify-center bg-accent-500 text-background-50 rounded-full text-sm font-bold flex-shrink-0">
                      {index + 1}
                    </span>
                    <h3 className="flex-1 font-heading text-base md:text-lg font-bold text-foreground-950">
                      {item.title}
                    </h3>
                    <span
                      className={`w-8 h-8 flex items-center justify-center rounded-full bg-background-200/70 text-foreground-700 transition-transform duration-300 flex-shrink-0 ${
                        isExpanded ? 'rotate-180' : ''
                      }`}
                    >
                      <i className="ri-arrow-down-s-line text-lg" />
                    </span>
                  </div>
                  <div
                    className={`grid transition-all duration-300 ease-in-out ${
                      isExpanded
                        ? 'grid-rows-[1fr] opacity-100 mt-3'
                        : 'grid-rows-[0fr] opacity-0'
                    }`}
                  >
                    <div className="overflow-hidden">
                      <p className="pl-14 text-sm md:text-base text-foreground-600 leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <div
          className={`mt-12 md:mt-16 max-w-4xl mx-auto transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="text-center mb-6">
            <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-2 inline-flex items-center justify-center gap-3">
              <span className="w-8 h-8 flex items-center justify-center">
                <i className="ri-double-quotes-l text-accent-300 text-2xl"></i>
              </span>
              {t('guardian.title')}
              <span className="w-8 h-8 flex items-center justify-center">
                <i className="ri-double-quotes-r text-accent-300 text-2xl"></i>
              </span>
            </h3>
          </div>

          <div className="px-6 pt-2 pb-4 md:px-10 md:pt-2 md:pb-4 text-center relative">
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed max-w-2xl mx-auto">
              {t('guardian.desc')}
            </p>
          </div>

          <div className="mt-2 flex flex-wrap justify-center gap-3">
            <span className="inline-flex items-center gap-1.5 bg-accent-100 text-accent-800 px-3 py-1.5 rounded-full text-xs md:text-sm font-medium">
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-file-list-3-line text-xs"></i>
              </span>
              상속재산분할협의
            </span>
            <span className="inline-flex items-center gap-1.5 bg-accent-100 text-accent-800 px-3 py-1.5 rounded-full text-xs md:text-sm font-medium">
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-shield-check-line text-xs"></i>
              </span>
              한정승인
            </span>
            <span className="inline-flex items-center gap-1.5 bg-accent-100 text-accent-800 px-3 py-1.5 rounded-full text-xs md:text-sm font-medium">
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-user-settings-line text-xs"></i>
              </span>
              성년후견
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}